// API クライアント

interface User {
  id: string
  display_name: string
  picture_url?: string
  role: 'staff' | 'admin'
  hourly_wage: number
}

interface Attendance {
  id: number
  user_id: string
  date: string
  clock_in?: string
  clock_out?: string
  break_min: number
  work_min?: number
}

interface ShiftSlot {
  id: number
  date: string
  slot_type: 'evening' | 'night' | 'fullshift'
  start_time: string
  end_time: string
}

interface ShiftRequest {
  id: number
  user_id: string
  slot_id: number
  status: 'requested' | 'approved' | 'rejected'
  note?: string
  created_at: string
  display_name?: string
  date?: string
  slot_type?: string
  start_time?: string
  end_time?: string
}

interface WeeklyShiftRequest {
  id: number
  user_id: string
  date: string
  start_time: string
  end_time: string
  status: 'requested' | 'approved' | 'rejected'
  note?: string
  week_start: string
  created_at: string
  updated_at: string
}

interface Notice {
  id: number
  title: string
  body: string
  is_urgent: number
  author_id: string
  author_name?: string
  created_at: string
}

interface SalaryDetail {
  month: string
  employeeName: string
  hourlyWage: number
  totalWorkHours: number
  totalAmount: number
  dailyDetails: Array<{
    date: string
    startTime: string
    endTime: string
    workHours: number
    amount: number
  }>
}

interface SalaryList {
  month: string
  employees: Array<{
    userId: string
    name: string
    hourlyWage: number
    totalWorkHours: number
    totalAmount: number
  }>
  grandTotal: number
}

interface ApiResponse {
  success: boolean
  error?: string
  [key: string]: any
}

class ApiClient {
  private baseUrl: string
  private accessToken: string | null = null

  constructor(baseUrl: string = 'https://cafe-api.and-cafe.workers.dev/api/v1') {
    this.baseUrl = baseUrl
  }

  setAccessToken(token: string | null) {
    this.accessToken = token
  }

  private async request(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse> {
    const url = `${this.baseUrl}${endpoint}`
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>
    }

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      return data
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error)
      throw error
    }
  }

  // 認証関連
  async getMe(): Promise<ApiResponse & { user: User }> {
    return this.request('/auth/me') as any
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse & { user: User }> {
    return this.request('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data)
    }) as any
  }

  // 出退勤関連
  async clockIn(): Promise<ApiResponse & { message: string; time: string }> {
    return this.request('/attendance/clock-in', {
      method: 'POST'
    }) as any
  }

  async clockOut(): Promise<ApiResponse & { 
    message: string
    time: string
    totalWorkMin: number
    breakMin: number
    workMin: number
  }> {
    return this.request('/attendance/clock-out', {
      method: 'POST'
    }) as any
  }

  async breakStart(): Promise<ApiResponse & { message: string; time: string }> {
    return this.request('/attendance/break-start', {
      method: 'POST'
    }) as any
  }

  async breakEnd(): Promise<ApiResponse & { 
    message: string
    time: string
    breakDuration: number
    totalBreakMin: number
  }> {
    return this.request('/attendance/break-end', {
      method: 'POST'
    }) as any
  }

  async getTodayAttendance(): Promise<ApiResponse & {
    attendance: Attendance | null
    ongoingBreak: any | null
    currentTime: string
  }> {
    return this.request('/attendance/today') as any
  }

  async getAttendanceHistory(month?: string): Promise<ApiResponse & {
    month: string
    attendances: Attendance[]
  }> {
    const query = month ? `?month=${month}` : ''
    return this.request(`/attendance${query}`) as any
  }

  // シフト関連
  async getShifts(week: string): Promise<ApiResponse & {
    week: string
    dates: string[]
    slots: ShiftSlot[]
    requests: ShiftRequest[]
  }> {
    return this.request(`/shifts?week=${week}`) as any
  }

  async createShiftSlot(data: {
    date: string
    slotType: string
    startTime: string
    endTime: string
  }): Promise<ApiResponse & { slotId: number }> {
    console.log('API Client - createShiftSlot called with:', data)
    const result = this.request('/shifts/slots', {
      method: 'POST',
      body: JSON.stringify(data)
    }) as any
    console.log('API Client - createShiftSlot result:', result)
    return result
  }

  async submitShiftRequest(slotId: number, note?: string): Promise<ApiResponse> {
    return this.request('/shifts/request', {
      method: 'POST',
      body: JSON.stringify({ slotId, note })
    }) as any
  }

  async cancelShiftRequest(slotId: number): Promise<ApiResponse> {
    return this.request(`/shifts/request/${slotId}`, {
      method: 'DELETE'
    }) as any
  }

  async approveShifts(requests: { requestId: number; status: 'approved' | 'rejected' }[]): Promise<ApiResponse & {
    notificationsSent: number
  }> {
    return this.request('/shifts/approve', {
      method: 'PUT',
      body: JSON.stringify({ requests })
    }) as any
  }

  // 週次シフト希望（新API）
  async getWeeklyShifts(weekStart: string): Promise<ApiResponse & {
    requests: WeeklyShiftRequest[]
  }> {
    return this.request(`/shifts/weekly?week_start=${weekStart}`) as any
  }

  async submitWeeklyShifts(weekStart: string, entries: {
    date: string
    startTime: string
    endTime: string
    note?: string
  }[]): Promise<ApiResponse> {
    return this.request('/shifts/weekly', {
      method: 'POST',
      body: JSON.stringify({ weekStart, entries })
    }) as any
  }

  async getAllWeeklyShifts(weekStart: string): Promise<ApiResponse & {
    requests: (WeeklyShiftRequest & { display_name: string })[]
  }> {
    return this.request(`/shifts/weekly/all?week_start=${weekStart}`) as any
  }

  async approveWeeklyShifts(requests: { requestId: number; status: 'approved' | 'rejected' }[]): Promise<ApiResponse & {
    notificationsSent: number
  }> {
    return this.request('/shifts/weekly/approve', {
      method: 'PUT',
      body: JSON.stringify({ requests })
    }) as any
  }

  async getMyShifts(week: string): Promise<ApiResponse & {
    week: string
    dates: string[]
    shifts: ShiftRequest[]
  }> {
    return this.request(`/shifts/my?week=${week}`) as any
  }

  // 連絡板関連
  async getNotices(): Promise<ApiResponse & { notices: Notice[] }> {
    return this.request('/notices') as any
  }

  async createNotice(data: { title: string; body: string; is_urgent?: boolean }): Promise<ApiResponse & { notice: Notice }> {
    return this.request('/notices', {
      method: 'POST',
      body: JSON.stringify(data)
    }) as any
  }

  async deleteNotice(id: number): Promise<ApiResponse> {
    return this.request(`/notices/${id}`, {
      method: 'DELETE'
    }) as any
  }

  // 給与関連
  async getSalary(month: string): Promise<ApiResponse & { salary: SalaryDetail }> {
    return this.request(`/salary?month=${month}`) as any
  }

  async getAllSalary(month: string): Promise<ApiResponse & { salaryList: SalaryList }> {
    return this.request(`/salary/all?month=${month}`) as any
  }

  async downloadSalaryCsv(month: string): Promise<Response> {
    const url = `${this.baseUrl}/salary/csv?month=${month}`
    const headers: Record<string, string> = {}
    
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }
    
    return fetch(url, { headers })
  }

  // スタッフ管理関連
  async getAllStaff(): Promise<ApiResponse & { staff: User[] }> {
    return this.request('/staff') as any
  }

  async updateStaffWage(userId: string, hourlyWage: number): Promise<ApiResponse & {
    userId: string
    hourlyWage: number
  }> {
    return this.request(`/staff/${userId}/wage`, {
      method: 'PUT',
      body: JSON.stringify({ hourlyWage })
    }) as any
  }
}

export const apiClient = new ApiClient()
export type { User, Attendance, ApiResponse, ShiftSlot, ShiftRequest, WeeklyShiftRequest, Notice, SalaryDetail, SalaryList }